import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { useFeatureFlagSplit } from '@verantos-dev/app-state-base';
import { useCohortDefinitionStore } from '@verantos-dev/app-state-cohort-definition';
import {
  useDiscardDraftMutation,
  useRevertCohortMutation,
  useSafeRouteParams,
  type DiscardDraftResponse,
} from '@verantos-dev/data-access-cohorts';
import { avKeys, cohortDefinitionToQueryData } from '@verantos-dev/feature-analytical-variables';
import { useResearchAssistantSocketContext } from '@verantos-dev/feature-research-assistant';
import { CohortDefinitionStatus } from '@verantos-dev/types-cohorts';

import { useCohortBuilderModalContext } from '../../contexts/CohortBuilderModalContext';

/**
 * Modal orchestration for cohort revert.
 *
 * Two transport paths live here, behind `research_assistant_gen_3`:
 *
 * - **Gen-3 (ff on)** — `useDiscardDraftMutation` (REST DELETE) clears the
 *   draft cache and returns the now-current definition + status. The store is
 *   hydrated directly from that payload and the version-keyed definition cache
 *   is seeded to match — no refetch round-trip.
 * - **Legacy (ff off)** — `useRevertCohortMutation` does the original
 *   socket emit + `cohort_reverted` ack. Behavior unchanged.
 *
 * TODO: cleanup with research_assistant_gen_3 ff (collapse to the REST path
 * and delete the socket branch).
 */
export const useRevertCohortDefinitionModal = () => {
  const [countDown, setCountDown] = useState(3);
  const intl = useIntl();
  const { socket } = useResearchAssistantSocketContext();
  const { projectId, cohortId, cohortVersionId: routeVersionId } = useSafeRouteParams();
  const { closeDialog } = useCohortBuilderModalContext();
  const isGen3 = useFeatureFlagSplit('research_assistant_gen_3', false);

  const socketMutation = useRevertCohortMutation();
  const draftMutation = useDiscardDraftMutation();
  const queryClient = useQueryClient();

  const activeMutation = isGen3 ? draftMutation : socketMutation;
  const {
    isPending: isSubmitting,
    isSuccess: isSubmitSuccess,
    error: submittingError,
  } = activeMutation;

  const onClose = () => {
    closeDialog();
  };

  // Reconcile the Zustand store from the REST response. The discard payload
  // carries the authoritative `definition` to revert to, so we hydrate the
  // store directly from it rather than refetching by version.
  //
  // This matters for the explicit_save path: there the discard is a pure cache
  // clear with no version change, so the backend returns `versionId: null` (and
  // the gen-3 detail route has no version segment, so `routeVersionId` is
  // undefined too). The old refetch branch keyed on `versionId` then early-
  // returned and never reconciled the store, leaving the criteria/AVs the user
  // edited locally on screen until a manual page reload.
  const applyDiscardResponse = useCallback(
    async (response: DiscardDraftResponse) => {
      const store = useCohortDefinitionStore.getState();
      store.setIsSaving(false);
      useCohortDefinitionStore.setState({ hasStagedChanges: false });
      if (response.newStatus) {
        store.setProfileStatus(response.newStatus as CohortDefinitionStatus);
      }
      if (response.versionId) {
        useCohortDefinitionStore.setState((s) => ({
          profile: { ...s.profile, latestCohortVersionId: response.versionId ?? undefined },
        }));
      }

      // Hydrate the store from the reverted definition. `null` (no committed
      // version) maps to an empty definition, which is the correct revert
      // target for a cohort that was only ever a draft.
      const queryData = cohortDefinitionToQueryData(response.definition ?? undefined, false);
      store.setFromServer(
        queryData.analyticalVariables,
        queryData.criteria,
        queryData.studyPeriod,
        {
          simpleEvents: queryData.simpleEvents,
          derivedEvents: queryData.derivedEvents,
          indexDate: queryData.indexDate,
          subjectVariables: queryData.subjectVariables,
        }
      );

      // Keep the TanStack caches consistent so navigating away and back doesn't
      // re-read a stale draft entry. Seed the version-keyed definition cache
      // with the same transform a real fetch produces (byte-identical), and
      // invalidate the profile cache for the post-revert status / subject count.
      // Falls back to the store's version when the response omits it
      // (explicit_save).
      const versionId =
        response.versionId ??
        routeVersionId ??
        useCohortDefinitionStore.getState().profile.latestCohortVersionId;
      if (versionId) {
        queryClient.setQueryData(avKeys.list(versionId), queryData);
        void queryClient.invalidateQueries({ queryKey: avKeys.profile(versionId) });
      }
    },
    [queryClient, routeVersionId]
  );

  // Manual retry path — wired to the Retry button when submittingError is set.
  const onSubmit = useCallback(() => {
    if (!projectId || !cohortId) return;
    if (isGen3) {
      draftMutation.mutate({ projectId, cohortId }, { onSuccess: applyDiscardResponse });
      return;
    }
    if (!socket) return;
    socketMutation.mutate({ projectId, cohortId, socket });
  }, [projectId, cohortId, isGen3, draftMutation, socketMutation, applyDiscardResponse, socket]);

  // Auto-submit once on mount. setTimeout(0) + cleanup dedups under React 18
  // StrictMode double-mount.
  useEffect(() => {
    if (!projectId || !cohortId) return;
    if (!isGen3 && !socket) return;

    const timerId = setTimeout(() => {
      if (isGen3) {
        draftMutation.mutate({ projectId, cohortId }, { onSuccess: applyDiscardResponse });
      } else if (socket) {
        socketMutation.mutate({ projectId, cohortId, socket });
      }
    }, 0);
    return () => clearTimeout(timerId);
    // Intentionally empty dep array — fire once per mount, like the legacy
    // socket-emit version. Mutation refs are stable across renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Countdown + auto-close on success
  useEffect(() => {
    if (isSubmitSuccess && countDown > 0) {
      const interval = setInterval(() => {
        setCountDown((prev) => prev - 1);
        if (countDown === 1) {
          clearInterval(interval);
          closeDialog();
        }
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [closeDialog, countDown, isSubmitSuccess]);

  return {
    intl,
    title: intl.formatMessage({ id: 'projects.cohortBuilderPage.header.undoButton' }),
    countDown,
    onClose,
    onSubmit,
    submittingError,
    isSubmitting,
    isSubmitSuccess,
  };
};
